import type {
  TCreateCompanyInputDTO,
  TCreateCompanyResponseBodyDTO,
  TExposedCompanyDTO,
  TFindAllCompaniesResponseBodyDTO,
  TFindCompanyByIdResponseBodyDTO,
  TUpdateCompanyInputDTO,
} from '@doutorsim/shared/companies'
import type {
  TCreateFacilityInputDTO,
  TExposedFacilityDTO,
  TFindFacilitiesByCompanyIdResponseBodyDTO,
  TUpdateFacilityInputDTO,
} from '@doutorsim/shared/facilities'
import type {
  TExposedUserDTO,
  TFindUsersInputDTO,
  TUpdateUserInputDTO,
} from '@doutorsim/shared/users'
import type { TExposedBankDTO } from '@doutorsim/shared/exposed/banks'
import type { ApiRequestOptions } from './client'
import { ApiClient } from './client'
import { applicationsApi } from './entities/applications'
import { companyApplicationsApi } from './entities/applications.company'
import { staffApplicationsApi } from './entities/applications.staff'
import { attachmentsApi } from './entities/attachments'
import { personsApi } from './entities/persons'
import { staffPersonsApi } from './entities/persons.staff'

export const apiClient = new ApiClient()

export const api = {
  applications: applicationsApi,
  attachments: attachmentsApi,
  companyApplications: companyApplicationsApi,
  persons: personsApi,

  auth: {
    signup: (data: unknown) => apiClient.post('/api/v1/auth/sign-up/email', data),
  },

  banks: {
    list: () => apiClient.get<TExposedBankDTO[]>('/api/v1/banks'),
  },

  companies: {
    list: () => apiClient.get<TExposedCompanyDTO[]>('/api/v1/companies'),
    get: (id: string) => apiClient.get<TFindCompanyByIdResponseBodyDTO>(`/api/v1/companies/${id}`),
    getBySlug: (slug: string) =>
      apiClient.get<TExposedCompanyDTO>(`/api/v1/companies/by-slug/${slug}`),
    switch: (id: string) => apiClient.put('/api/v1/auth/user/company', { companyId: id }),
    update: (data: TUpdateCompanyInputDTO, options?: ApiRequestOptions) =>
      apiClient.put<TCreateCompanyResponseBodyDTO>(`/api/v1/companies/${data.id}`, data, options),
    facilities: {
      list: (companyId: string) =>
        apiClient.get<TExposedFacilityDTO[]>(`/api/v1/companies/${companyId}/facilities`),
      get: (companyId: string, facilityId: string) =>
        apiClient.get<TExposedFacilityDTO>(
          `/api/v1/companies/${companyId}/facilities/${facilityId}`,
        ),
      getBySlug: (companyId: string, slug: string) =>
        apiClient.get<TExposedFacilityDTO>(
          `/api/v1/companies/${companyId}/facilities/by-slug/${slug}`,
        ),
      update: (
        companyId: string,
        facilityId: string,
        data: Omit<TUpdateFacilityInputDTO, 'id' | 'companyId'>,
      ) =>
        apiClient.put<TExposedFacilityDTO>(
          `/api/v1/companies/${companyId}/facilities/${facilityId}`,
          data,
        ),
    },
  },

  users: {
    list: (data: TFindUsersInputDTO, options?: ApiRequestOptions) => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(data)) {
        if (v != null) params.set(k, String(v))
      }
      const search = params.toString()

      return apiClient.get<TExposedUserDTO[]>(`/api/v1/users${search && `?${search}`}`, options)
    },
    update: (data: TUpdateUserInputDTO) =>
      apiClient.put<TExposedUserDTO>(`/api/v1/users/${data.id}`, data),
    delete: (id: string) =>
      apiClient.delete<{ id: string; email: string; message: string }>(
        `/api/v1/auth/integrators/${id}`,
      ),
  },

  staff: {
    applications: staffApplicationsApi,
    persons: staffPersonsApi,
    companies: {
      list: () => apiClient.get<TFindAllCompaniesResponseBodyDTO>('/api/v1/staff/companies'),
      get: (id: string) => apiClient.get(`/api/v1/staff/companies/${id}`),
      create: async (data: TCreateCompanyInputDTO) => {
        const result = await apiClient.post<TCreateCompanyResponseBodyDTO>(
          '/api/v1/staff/companies',
          data,
        )
        return result
      },
      update: (id: string, data: unknown) => apiClient.put(`/api/v1/staff/companies/${id}`, data),
    },
    facilities: {
      list: () =>
        apiClient.get<TFindFacilitiesByCompanyIdResponseBodyDTO>('/api/v1/staff/facilities'),
      get: (id: string) => apiClient.get(`/api/v1/staff/facilities/${id}`),
      create: (data: TCreateFacilityInputDTO) =>
        apiClient.post(`/api/v1/staff/facilities/companies/${data.companyId}/facilities`, data),
      update: (id: string, data: unknown) => apiClient.put(`/api/v1/staff/facilities/${id}`, data),
    },
    users: {
      list: () => apiClient.get('/api/v1/staff/users'),
      get: (id: string) => apiClient.get(`/api/v1/staff/users/${id}`),
    },
    invitations: {
      invite: (
        data: { email: string; companyId: string; role: string },
        options?: ApiRequestOptions,
      ) => apiClient.post('/api/v1/staff/invitations/invite', data, options),
    },
  },
}
